import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Button } from "components/Button";
import { Card } from "components/Card/Card";
import { Checkbox } from "components/Checkbox/Checkbox";
import { LoadingSpinner } from "components/LoadingSpinner";
import { Modal } from "components/Modal";
import { ModalRenderType } from "components/Modal/Modal.model";
import {
  type EmailType,
  MultipleEmailInput,
} from "components/MultipleEmailInput/MultipleEmailInput";
import { StatusTag } from "components/StatusTag/StatusTag";
import { TestType } from "constants/exam-constants";
import { modalIds } from "constants/modals-constants";
import { useModal } from "hooks/useModal";
import { useScheduledTests } from "hooks/useScheduledTests";

import { Heading2, Heading3 } from "styles/elements/Headings";
import { ParagraphLarge } from "styles/elements/Texts";
import { Box, CssGrid } from "styles/helpers/layout";
import {
  StyledAccordionItem,
  StyledButtonContainer,
  StyledColumn,
  StyledColumnHeader,
  StyledDivider,
  StyledQuestionPreview,
  StyledSpinner,
  StyledTestRecalculationModal,
} from "./ScheduledTestRecalculationModal.styled";

export interface ScheduledTestRecalculationModalProps {
  scheduledTestId?: number;
}

export const ScheduledTestRecalculationModal: React.FC<ScheduledTestRecalculationModalProps> =
  observer(({ scheduledTestId }) => {
    const { isModalOpen, closeModal } = useModal();
    const {
      fetchScheduledTestContentOverview,
      invalidateScheduledTestQuestions,
      scheduledTestContentOverview: test,
      loading,
    } = useScheduledTests();
    const { t } = useTranslation("admin-environment");

    const [questionIds, setQuestionIds] = useState<number[]>([]);
    const [recalculateCertificates, setRecalculateCertificates] = useState<boolean>(false);
    const [recalculateForSpecificStudents, setRecalculateForSpecificStudents] =
      useState<boolean>(false);
    const [userEmails, setUserEmails] = useState<EmailType[]>([]);

    const isOpen = isModalOpen(modalIds.testRecalculationModal);

    useEffect(() => {
      if (isOpen && scheduledTestId) {
        fetchScheduledTestContentOverview(+scheduledTestId);
      }
    }, [scheduledTestId, isOpen, fetchScheduledTestContentOverview]);

    const toggleQuestionSelection = (questionId: number) => {
      setQuestionIds((prev) =>
        prev.includes(questionId) ? prev.filter((id) => id !== questionId) : [...prev, questionId],
      );
    };

    const triggerRecalculation = () => {
      invalidateScheduledTestQuestions(
        Number(scheduledTestId),
        recalculateCertificates,
        questionIds,
        // We only pass along user emails parameter if the corresponding checkbox was enabled
        recalculateForSpecificStudents ? userEmails.map(({ email }) => email) : undefined,
      );

      // Automatically close modal and reset form after submit
      clearData();
    };

    const clearData = () => {
      setQuestionIds([]);
      setRecalculateCertificates(false);
      setRecalculateForSpecificStudents(false);
      setUserEmails([]);
      closeModal();
    };

    // We should only allow user to submit the form if there's at least one question selected
    // and, if the option to invalidate for specific students is checked, all emails are valid
    const shouldAllowSubmit =
      questionIds.length > 0 &&
      (!recalculateForSpecificStudents ||
        (userEmails.length > 0 && !userEmails.some(({ isIncorrect }) => isIncorrect)));

    return (
      <Modal
        showCloseButton
        dataCy="scheduled-test-recalculation-modal"
        isOpen={isModalOpen(modalIds.testRecalculationModal)}
        renderToElementType={ModalRenderType.REACT_PORTAL}
        onClose={clearData}
      >
        <StyledTestRecalculationModal>
          {loading ? (
            <StyledSpinner>
              <LoadingSpinner />
            </StyledSpinner>
          ) : (
            <>
              <Heading2 mb={2}>{test?.title}</Heading2>
              <ParagraphLarge mb={4}>
                <Trans i18nKey="adminTools.scheduledTest.recalculationModal.description" t={t}>
                  Below you'll find a list of all the questions of the exam. Please, choose the
                  questions that will be marked as correct for all the students who answered it.
                  <br />
                  <br />
                  Keep in mind that if you select a correct text question, all words within the test
                  will be marked as correct. This means that the increase in the scores of students
                  who made mistakes could be bigger than in a regular question.
                </Trans>
              </ParagraphLarge>
              {/* -- Overview of questions per exercise -- */}
              <Card>
                {test?.exercises.map((exercise) => (
                  <StyledAccordionItem
                    key={exercise.id}
                    title={exercise.title}
                    dataCy="modal-exercise-container"
                  >
                    <CssGrid
                      alignItems="center"
                      gridTemplateColumns="3rem repeat(4, 1fr)"
                      gap={1.5}
                    >
                      <StyledColumnHeader>#</StyledColumnHeader>
                      <StyledColumnHeader>
                        {t(
                          "adminTools.scheduledTest.recalculationModal.questionOverview.columns.legacyId",
                        )}
                      </StyledColumnHeader>
                      <StyledColumnHeader>
                        {t(
                          "adminTools.scheduledTest.recalculationModal.questionOverview.columns.questionType",
                        )}
                      </StyledColumnHeader>
                      <StyledColumnHeader>
                        {t(
                          "adminTools.scheduledTest.recalculationModal.questionOverview.columns.maxPoints",
                        )}
                      </StyledColumnHeader>
                      <StyledColumnHeader>
                        {t(
                          "adminTools.scheduledTest.recalculationModal.questionOverview.columns.markAsInvalid",
                        )}
                      </StyledColumnHeader>
                    </CssGrid>
                    {exercise.questions.map((question, index) => {
                      return (
                        <Box key={question.id} mb={index === exercise.questions.length - 1 ? 3 : 0}>
                          <StyledDivider height={1} mb={1} />
                          <CssGrid
                            alignItems="center"
                            data-cy="modal-question-container"
                            gridTemplateColumns="3rem repeat(4, 1fr)"
                            columnGap={1.5}
                            rowGap={0.5}
                          >
                            <StyledColumn>{index + 1}</StyledColumn>
                            <StyledColumn>{question.legacyId}</StyledColumn>
                            <StyledColumn>
                              <StatusTag label={question.type} />
                            </StyledColumn>
                            <StyledColumn textAlign="center">{question.maxPoints}</StyledColumn>
                            <StyledColumn textAlign="center">
                              <Checkbox
                                aria-label={t(
                                  "adminTools.scheduledTest.recalculationModal.questionOverview.invalidateQuestion",
                                )}
                                dataCy={`invalidate-question-${question.id}-checkbox`}
                                checked={questionIds.includes(question.id)}
                                onChange={() => toggleQuestionSelection(question.id)}
                                id={`question-${question.id}`}
                              />
                            </StyledColumn>
                            <StyledQuestionPreview
                              dataCy={`question-${question.id}-content-preview`}
                              dirtyHtml={question.content}
                            />
                          </CssGrid>
                        </Box>
                      );
                    })}
                  </StyledAccordionItem>
                ))}
              </Card>
              {/* -- Additional recalculation options -- */}
              <Heading3 mt={4} mb={2}>
                {t("adminTools.scheduledTest.recalculationModal.additionalOptions.title")}
              </Heading3>
              <Checkbox
                aria-label={t(
                  "adminTools.scheduledTest.recalculationModal.additionalOptions.options.recalculateForSpecificUsers",
                )}
                dataCy="recalculate-for-specific-users-checkbox"
                checked={recalculateForSpecificStudents}
                id="recalculate-for-specific-users-option"
                label={t(
                  "adminTools.scheduledTest.recalculationModal.additionalOptions.options.recalculateForSpecificUsers",
                )}
                onChange={(e) => setRecalculateForSpecificStudents(e.target.checked)}
              />
              {recalculateForSpecificStudents && (
                <Box mt={-1} mb={3}>
                  <MultipleEmailInput
                    dataCy="recalculation-candidate-emails"
                    height="10rem"
                    selectedEmails={userEmails}
                    onSelectedEmailsChanged={setUserEmails}
                  />
                </Box>
              )}
              {test?.testType === TestType.SUMMATIVE_TEST && (
                <Checkbox
                  aria-label={t(
                    "adminTools.scheduledTest.recalculationModal.additionalOptions.options.recalculateCertificates",
                  )}
                  dataCy="recalculate-certificates-checkbox"
                  checked={recalculateCertificates}
                  id="recalculate-certificates-option"
                  label={t(
                    "adminTools.scheduledTest.recalculationModal.additionalOptions.options.recalculateCertificates",
                  )}
                  onChange={() => setRecalculateCertificates(!recalculateCertificates)}
                />
              )}
              {/* -- Submit button -- */}
              <StyledButtonContainer>
                <Button
                  dataCy="scheduled-test-recalculation-button"
                  disabled={!shouldAllowSubmit}
                  variant="primary"
                  onClick={triggerRecalculation}
                >
                  {t("adminTools.scheduledTest.recalculation")}
                </Button>
              </StyledButtonContainer>
            </>
          )}
        </StyledTestRecalculationModal>
      </Modal>
    );
  });
